import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, Puzzle, Lightbulb, Check, X, Send, MessageCircle, Loader2, Shuffle, HelpCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const FB_URL = 'https://m.me/okiescape';

export default function WeeklyPuzzlePage() {
  const { t, lang } = useLanguage();
  const [puzzle, setPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {correct: bool}
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/puzzle/active`, { params: { lang } })
      .then(res => { setPuzzle(res.data.active ? res.data : null); setLoading(false); })
      .catch(() => setLoading(false));
  }, [lang]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !answer.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/puzzle/submit`, { name: name.trim(), email: email.trim(), answer: answer.trim() });
      setResult(data);
    } catch { setResult({ correct: false, error: true }); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #E32510, transparent 70%)' }} />
      </div>

      {/* Top bar */}
      <div className="sticky top-0 z-50 border-b border-neutral-800 bg-black/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link to="/" data-testid="puzzle-back-link" className="flex items-center gap-2 text-neutral-400 hover:text-brand font-heading text-xs tracking-widest uppercase transition-colors">
            <ArrowLeft size={14} /> {t('puzzleBackHome')}
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-20 relative z-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-brand font-heading text-xs tracking-[0.25em] uppercase mb-3">
            <Puzzle size={14} />
            <span>{t('puzzleLabel')}</span>
          </div>
          <h1 data-testid="puzzle-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-wide uppercase" style={{ textShadow: '0 0 40px rgba(227,37,16,0.2)' }}>
            {t('puzzleHeading')}
          </h1>
          <p className="font-sans text-base text-neutral-400 mt-3 max-w-lg mx-auto">
            {t('puzzleSubheading')}
          </p>
        </div>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 size={36} className="text-brand animate-spin" />
          </div>
        )}

        {!loading && !puzzle && (
          <div className="text-center py-16 border-2 border-dashed border-neutral-800 rounded-2xl">
            <Puzzle size={48} className="text-neutral-700 mx-auto mb-3" />
            <p className="font-heading text-lg tracking-wider uppercase text-neutral-500">{t('puzzleNoneTitle')}</p>
            <p className="font-sans text-sm text-neutral-600 mt-1">{t('puzzleNoneDesc')}</p>
          </div>
        )}

        {!loading && puzzle && !result && (
          <div className="space-y-6">
            {/* Puzzle card */}
            <div data-testid="puzzle-card" className="border border-neutral-800 rounded-2xl bg-neutral-900/60 p-6 sm:p-8">
              {puzzle.week_label && (
                <span className="font-heading text-[11px] tracking-widest uppercase text-brand/80 mb-3 block">{puzzle.week_label}</span>
              )}

              {/* Type badge */}
              <div className="flex items-center gap-2 mb-4">
                {puzzle.puzzle_type === 'riddle' && <HelpCircle size={18} className="text-brand" />}
                {puzzle.puzzle_type === 'word_scramble' && <Shuffle size={18} className="text-brand" />}
                {puzzle.puzzle_type === 'trivia' && <Lightbulb size={18} className="text-brand" />}
                <span className="font-heading text-sm tracking-widest uppercase text-neutral-300">
                  {puzzle.puzzle_type === 'riddle' ? t('puzzleTypeRiddle') : puzzle.puzzle_type === 'word_scramble' ? t('puzzleTypeScramble') : t('puzzleTypeTrivia')}
                </span>
              </div>

              {/* Question */}
              <p data-testid="puzzle-question" className="font-sans text-lg sm:text-xl text-white leading-relaxed mb-4">
                {puzzle.question}
              </p>

              {/* Scrambled letters for word scramble */}
              {puzzle.puzzle_type === 'word_scramble' && puzzle.scrambled && (
                <div data-testid="puzzle-scrambled" className="flex flex-wrap gap-2 mb-4">
                  {puzzle.scrambled.split('').map((ch, i) => (
                    <span key={i} className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg border-2 border-brand/50 bg-brand/10 font-heading text-xl sm:text-2xl uppercase text-white">
                      {ch}
                    </span>
                  ))}
                </div>
              )}

              {/* Hint toggle */}
              {puzzle.hint && (
                <button
                  onClick={() => setShowHint(!showHint)}
                  data-testid="puzzle-hint-btn"
                  className="flex items-center gap-1.5 font-heading text-xs tracking-widest uppercase text-neutral-500 hover:text-yellow-500 transition-colors mb-4"
                >
                  <Lightbulb size={13} /> {showHint ? t('puzzleHideHint') : t('puzzleNeedHint')}
                </button>
              )}
              {showHint && puzzle.hint && (
                <div data-testid="puzzle-hint-text" className="border border-yellow-700/40 bg-yellow-900/10 rounded-xl p-3 mb-4">
                  <p className="font-sans text-sm text-yellow-200/80 italic">{puzzle.hint}</p>
                </div>
              )}
            </div>

            {/* Submit form */}
            <form onSubmit={submit} data-testid="puzzle-form" className="border border-neutral-800 rounded-2xl bg-neutral-900/60 p-6 sm:p-8 space-y-4">
              <h3 className="font-heading text-lg tracking-wider uppercase text-white mb-2">{t('puzzleSubmitTitle')}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder={t('puzzleNameInput')}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  data-testid="puzzle-name-input"
                  className="w-full bg-black border border-neutral-700 rounded-xl px-4 py-3 font-sans text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-brand transition-colors"
                />
                <input
                  type="email"
                  placeholder={t('puzzleEmailInput')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  data-testid="puzzle-email-input"
                  className="w-full bg-black border border-neutral-700 rounded-xl px-4 py-3 font-sans text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-brand transition-colors"
                />
              </div>
              <input
                type="text"
                placeholder={t('puzzleAnswerInput')}
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                data-testid="puzzle-answer-input"
                className="w-full bg-black border border-neutral-700 rounded-xl px-4 py-3 font-sans text-base text-white placeholder-neutral-600 focus:outline-none focus:border-brand transition-colors"
              />
              <button
                type="submit"
                disabled={submitting || !name.trim() || !email.trim() || !answer.trim()}
                data-testid="puzzle-submit-btn"
                className="w-full flex items-center justify-center gap-2 font-heading text-sm tracking-widest uppercase bg-brand text-white py-4 rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-40"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {submitting ? t('puzzleChecking') : t('puzzleSubmitBtn')}
              </button>
            </form>
          </div>
        )}

        {/* Result */}
        {result && (
          <div data-testid="puzzle-result" className="space-y-6">
            <div className={`border-2 rounded-2xl p-8 text-center ${result.correct ? 'border-green-500/50 bg-green-900/10' : 'border-brand/50 bg-brand/5'}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${result.correct ? 'bg-green-500/20' : 'bg-brand/20'}`}>
                {result.correct ? <Check size={32} className="text-green-400" /> : <X size={32} className="text-brand" />}
              </div>
              <h2 className="font-heading text-3xl tracking-wider uppercase mb-2">
                {result.correct ? t('puzzleCorrect') : t('puzzleWrong')}
              </h2>
              <p className="font-sans text-base text-neutral-400">
                {result.correct ? t('puzzleCorrectDesc') : t('puzzleWrongDesc')}
              </p>
            </div>

            {/* Facebook DM CTA */}
            <a
              href={`${FB_URL}?text=${encodeURIComponent(`Hi! I just solved the Weekly Puzzle on your website! My name is ${name} and I'd like to enter the contest.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="puzzle-facebook-btn"
              className="flex items-center justify-center gap-3 w-full py-5 rounded-2xl bg-[#1877F2] hover:bg-[#166FE5] transition-colors font-heading text-lg tracking-wider uppercase text-white"
            >
              <MessageCircle size={22} /> {t('puzzleFacebookBtn')}
            </a>

            {!result.correct && (
              <button
                onClick={() => { setResult(null); setAnswer(''); }}
                data-testid="puzzle-retry-btn"
                className="w-full py-4 rounded-xl border-2 border-neutral-700 font-heading text-sm tracking-widest uppercase text-neutral-300 hover:border-brand hover:text-brand transition-colors"
              >
                {t('puzzleRetryBtn')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
